import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    password: '',
    password_confirmation: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authAPI.forgotPassword(formData.email);
      if (response.data.success) {
        setSuccess('Verification code has been sent to your email');
        setStep(2);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authAPI.verifyResetCode(formData.email, formData.code);
      if (response.data.success) {
        setSuccess('Code verified successfully');
        setStep(3);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.resetPassword(
        formData.email,
        formData.code,
        formData.password,
        formData.password_confirmation
      );
      if (response.data.success) {
        setSuccess('Password reset successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(10px, 3vw, 20px)'
    }}>
      <div style={{
        maxWidth: '450px',
        width: '100%',
        padding: 'clamp(30px, 8vw, 40px)',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{
          fontSize: 'clamp(1.8rem, 5vw, 2.2rem)',
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'center',
          marginBottom: 'clamp(20px, 5vw, 30px)'
        }}>
          {step === 1 && 'Forgot Password'}
          {step === 2 && 'Enter Verification Code'}
          {step === 3 && 'Reset Password'}
        </h1>

        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: 'clamp(10px, 3vw, 15px)',
            borderRadius: '8px',
            marginBottom: 'clamp(15px, 3vw, 20px)',
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
            border: '1px solid #f5c6cb',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '1.2rem' }}>⚠️</span>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: 'clamp(10px, 3vw, 15px)',
            borderRadius: '8px',
            marginBottom: 'clamp(15px, 3vw, 20px)',
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
            border: '1px solid #c3e6cb',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '1.2rem' }}>✅</span>
            {success}
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleSendCode}>
            <p style={{
              marginBottom: 'clamp(20px, 4vw, 30px)',
              fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
              color: '#666',
              textAlign: 'center'
            }}>
              Enter your email address and we'll send you a verification code to reset your password.
            </p>
            <div style={{ marginBottom: 'clamp(20px, 4vw, 30px)' }}>
              <label style={{
                display: 'block',
                marginBottom: 'clamp(5px, 1vw, 8px)',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                fontWeight: '600',
                color: '#333'
              }}>
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: 'clamp(10px, 3vw, 12px)',
                  border: error ? '1px solid #dc3545' : '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease'
                }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: 'clamp(12px, 3vw, 15px)',
                backgroundColor: loading ? '#6c757d' : '#EDB404',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: 'clamp(1rem, 3vw, 1.1rem)',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyCode}>
            <p style={{
              marginBottom: 'clamp(20px, 4vw, 30px)',
              fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
              color: '#666',
              textAlign: 'center'
            }}>
              We've sent a 6-digit code to <strong>{formData.email}</strong>. Please enter it below.
            </p>
            <div style={{ marginBottom: 'clamp(20px, 4vw, 30px)' }}>
              <label style={{
                display: 'block',
                marginBottom: 'clamp(5px, 1vw, 8px)',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                fontWeight: '600',
                color: '#333'
              }}>
                Verification Code
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                maxLength={6}
                placeholder="000000"
                style={{
                  width: '100%',
                  padding: 'clamp(10px, 3vw, 12px)',
                  border: error ? '1px solid #dc3545' : '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease',
                  textAlign: 'center',
                  letterSpacing: '8px',
                  fontFamily: 'monospace'
                }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: 'clamp(12px, 3vw, 15px)',
                backgroundColor: loading ? '#6c757d' : '#EDB404',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: 'clamp(1rem, 3vw, 1.1rem)',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button
              type="button"
              onClick={() => setStep(1)}
              style={{
                width: '100%',
                padding: 'clamp(10px, 2.5vw, 12px)',
                marginTop: 'clamp(10px, 2.5vw, 15px)',
                backgroundColor: 'transparent',
                color: '#EDB404',
                border: '1px solid #EDB404',
                borderRadius: '8px',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Resend Code
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleResetPassword}>
            <p style={{
              marginBottom: 'clamp(20px, 4vw, 30px)',
              fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
              color: '#666',
              textAlign: 'center'
            }}>
              Enter your new password below.
            </p>
            <div style={{ marginBottom: 'clamp(15px, 3vw, 20px)' }}>
              <label style={{
                display: 'block',
                marginBottom: 'clamp(5px, 1vw, 8px)',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                fontWeight: '600',
                color: '#333'
              }}>
                New Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                style={{
                  width: '100%',
                  padding: 'clamp(10px, 3vw, 12px)',
                  border: error ? '1px solid #dc3545' : '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease'
                }}
              />
            </div>
            <div style={{ marginBottom: 'clamp(20px, 4vw, 30px)' }}>
              <label style={{
                display: 'block',
                marginBottom: 'clamp(5px, 1vw, 8px)',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                fontWeight: '600',
                color: '#333'
              }}>
                Confirm Password
              </label>
              <input
                type="password"
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleChange}
                required
                minLength={8}
                style={{
                  width: '100%',
                  padding: 'clamp(10px, 3vw, 12px)',
                  border: error ? '1px solid #dc3545' : '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease'
                }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: 'clamp(12px, 3vw, 15px)',
                backgroundColor: loading ? '#6c757d' : '#EDB404',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: 'clamp(1rem, 3vw, 1.1rem)',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        <p style={{
          textAlign: 'center',
          marginTop: 'clamp(20px, 4vw, 30px)',
          fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
          color: '#666'
        }}>
          Remember your password?{' '}
          <Link
            to="/login"
            style={{
              color: '#EDB404',
              textDecoration: 'none',
              fontWeight: '600'
            }}
          >
            Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;



